// iter-301 — Floating AI assistant.
//
// Shows a round bubble on every authenticated page. Click → opens a
// chat drawer with:
//   • Multi-turn text chat (keeps a session_id)
//   • 🎤 Push-to-talk voice input (MediaRecorder → /api/ai/voice-chat)
//   • 🔊 Autoplay the bot's audio reply
//   • Voice picker (loaded once from /api/ai/discovery)
//
// Lives on top of every route via mount in App.js, as long as the user
// has a `business_token` in localStorage. If no token → render nothing.

import { useState, useEffect, useRef, useCallback } from 'react';
import {
  Bot, X, Send, Mic, MicOff, Volume2, Loader2, Settings,
  Sparkles, StopCircle,
} from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

/* ─────────────── Hook: use mic recording ─────────────── */
function useMicRecorder() {
  const mediaRef = useRef(null);
  const chunksRef = useRef([]);
  const [recording, setRecording] = useState(false);

  const start = useCallback(async () => {
    const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
    const mime = MediaRecorder.isTypeSupported('audio/webm;codecs=opus')
      ? 'audio/webm;codecs=opus'
      : (MediaRecorder.isTypeSupported('audio/webm') ? 'audio/webm' : '');
    const rec = mime ? new MediaRecorder(stream, { mimeType: mime }) : new MediaRecorder(stream);
    chunksRef.current = [];
    rec.ondataavailable = (e) => { if (e.data?.size > 0) chunksRef.current.push(e.data); };
    rec.onstop = () => {
      stream.getTracks().forEach(t => t.stop());
      setRecording(false);
    };
    rec.start();
    mediaRef.current = rec;
    setRecording(true);
  }, []);

  const stop = useCallback(() => new Promise((resolve) => {
    const rec = mediaRef.current;
    if (!rec) { resolve(null); return; }
    rec.addEventListener('stop', () => {
      const blob = new Blob(chunksRef.current, { type: rec.mimeType || 'audio/webm' });
      resolve(blob);
    }, { once: true });
    try { rec.stop(); } catch { resolve(null); }
  }), []);

  return { recording, start, stop };
}

/* ─────────────── Main component ─────────────── */
export default function FloatingAssistant({ language = 'ar', isDarkMode = true }) {
  const isRTL = language === 'ar';
  const token = typeof window !== 'undefined' ? localStorage.getItem('business_token') : null;

  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState([]);          // {role: user|assistant, text, audio?}
  const [input, setInput] = useState('');
  const [busy, setBusy] = useState(false);
  const [sessionId, setSessionId] = useState('');
  const [voices, setVoices] = useState([]);
  const [voiceId, setVoiceId] = useState('default');
  const [showVoicePicker, setShowVoicePicker] = useState(false);
  const [autoplay, setAutoplay] = useState(true);

  const listRef = useRef(null);
  const audioRef = useRef(null);
  const { recording, start, stop } = useMicRecorder();

  // Load voices once.
  useEffect(() => {
    if (!token || !open || voices.length) return;
    axios.get(`${BACKEND_URL}/api/ai/discovery`, { headers: { Authorization: `Bearer ${token}` }, timeout: 10000 })
      .then(({ data }) => {
        setVoices(Array.isArray(data.voices) && data.voices.length ? data.voices : [{ id: 'default', name: 'Default', name_ar: 'افتراضي' }]);
      })
      .catch(() => setVoices([{ id: 'default', name: 'Default', name_ar: 'افتراضي' }]));
  }, [open, token, voices.length]);

  // Autoscroll chat on new message.
  useEffect(() => {
    if (listRef.current) listRef.current.scrollTop = listRef.current.scrollHeight;
  }, [messages, busy]);

  if (!token) return null;

  const pushMessage = (role, text, audio = null) => {
    setMessages((prev) => [...prev, { role, text, audio, ts: Date.now() }]);
  };

  const playAudioB64 = (b64, mime) => {
    if (!b64) return;
    const url = `data:${mime || 'audio/mpeg'};base64,${b64}`;
    if (audioRef.current) {
      audioRef.current.src = url;
      audioRef.current.play().catch(() => {});
    }
  };

  const sendText = async (textOverride) => {
    const text = (textOverride ?? input).trim();
    if (!text || busy) return;
    pushMessage('user', text);
    setInput('');
    setBusy(true);
    try {
      const { data } = await axios.post(
        `${BACKEND_URL}/api/ai/chat`,
        { message: text, context: { screen: window.location.pathname } },
        { headers: { Authorization: `Bearer ${token}` }, timeout: 90000 }
      );
      // Backend returns `{response, success}` for the legacy /ai/chat.
      const reply = data?.response || data?.text || '…';
      if (data?.session_id) setSessionId(data.session_id);
      pushMessage('assistant', reply);
    } catch (err) {
      const detail = err?.response?.data?.detail || err?.message || 'failed';
      pushMessage('assistant', (language === 'ar' ? '⚠️ تعذّر الرد: ' : '⚠️ Reply failed: ') + String(detail).slice(0, 160));
    } finally {
      setBusy(false);
    }
  };

  const toggleMic = async () => {
    if (busy) return;
    if (!recording) {
      try { await start(); }
      catch { toast.error(language === 'ar' ? 'تعذّر الوصول للميكروفون' : 'Microphone access denied'); }
      return;
    }
    const blob = await stop();
    if (!blob || blob.size < 500) {
      toast.error(language === 'ar' ? 'التسجيل قصير جداً' : 'Recording too short');
      return;
    }
    setBusy(true);
    const fd = new FormData();
    fd.append('audio', blob, `voice-${Date.now()}.webm`);
    fd.append('voice_id', voiceId);
    fd.append('locale', language);
    if (sessionId) fd.append('session_id', sessionId);
    fd.append('context_json', JSON.stringify({ screen: window.location.pathname }));
    try {
      const { data } = await axios.post(
        `${BACKEND_URL}/api/ai/voice-chat`,
        fd,
        { headers: { Authorization: `Bearer ${token}` }, timeout: 120000 }
      );
      const transcript = data?.text || '🎤 (voice)';
      const replyText = data?.reply_text || transcript;
      pushMessage('user', transcript);
      pushMessage('assistant', replyText, { b64: data?.audio_base64, mime: data?.mime_type });
      if (autoplay && data?.audio_base64) playAudioB64(data.audio_base64, data.mime_type);
    } catch (err) {
      const detail = err?.response?.data?.detail || err?.message || 'failed';
      pushMessage('assistant', (language === 'ar' ? '⚠️ تعذّر الصوت: ' : '⚠️ Voice failed: ') + String(detail).slice(0, 160));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div dir={isRTL ? 'rtl' : 'ltr'} className="pointer-events-none">
      <audio ref={audioRef} className="hidden" preload="none" />

      {/* ── Floating bubble ── */}
      {!open && (
        <button
          onClick={() => setOpen(true)}
          data-testid="assistant-bubble"
          className={`pointer-events-auto fixed ${isRTL ? 'left-5' : 'right-5'} bottom-20 z-[80] h-16 w-16 rounded-full flex items-center justify-center hover:scale-110 active:scale-95 transition-all duration-300 group`}
          style={{ filter: 'drop-shadow(0 8px 24px rgba(30,155,217,0.5))' }}
          aria-label={language === 'ar' ? 'المساعد الذكي' : 'AI assistant'}
        >
          <img
            src="/cloudgm-ai-orb.png"
            alt="CloudGM AI"
            className="w-16 h-16 rounded-full group-hover:rotate-12 transition-transform duration-500"
          />
          <span className="absolute -top-0.5 -end-0.5 h-3.5 w-3.5 rounded-full bg-emerald-400 border-2 border-white dark:border-[#0F172A] animate-pulse" />
        </button>
      )}

      {/* ── Drawer ── */}
      {open && (
        <div
          data-testid="assistant-drawer"
          className={`pointer-events-auto fixed ${isRTL ? 'left-0' : 'right-0'} bottom-0 z-[85] w-full sm:${isRTL ? 'left-5' : 'right-5'} sm:bottom-5 sm:w-[380px] h-[70vh] sm:h-[560px] rounded-t-2xl sm:rounded-2xl overflow-hidden shadow-2xl border bg-[#0B1120] border-white/10 flex flex-col`}
        >
          {/* Header */}
          <div className="flex items-center justify-between gap-2 px-4 py-3 border-b border-white/10 bg-gradient-to-r from-[#38BDF8]/15 to-[#F97316]/15">
            <div className="flex items-center gap-2 min-w-0">
              <img
                src="/cloudgm-ai-orb.png"
                alt="AI"
                className="h-9 w-9 rounded-full flex-shrink-0"
                style={{ filter: 'drop-shadow(0 0 10px rgba(30,155,217,0.55))' }}
              />
              <div className="min-w-0">
                <p className="text-white font-bold text-sm leading-tight">
                  {language === 'ar' ? 'مساعد CloudGM' : 'CloudGM Assistant'}
                </p>
                <p className="text-[10px] text-emerald-300/80 leading-tight flex items-center gap-1">
                  <span className="h-1.5 w-1.5 rounded-full bg-emerald-400 animate-pulse" />
                  {language === 'ar' ? 'جاهز للمساعدة' : 'Ready'}
                </p>
              </div>
            </div>
            <div className="flex items-center gap-1">
              <button
                onClick={() => setShowVoicePicker((v) => !v)}
                className="h-8 w-8 rounded-full flex items-center justify-center text-white/70 hover:text-white hover:bg-white/10"
                data-testid="assistant-voice-settings"
                aria-label="voice settings"
              >
                <Settings className="h-4 w-4" />
              </button>
              <button
                onClick={() => setOpen(false)}
                className="h-8 w-8 rounded-full flex items-center justify-center text-white/70 hover:text-white hover:bg-white/10"
                data-testid="assistant-close"
                aria-label="close"
              >
                <X className="h-4 w-4" />
              </button>
            </div>
          </div>

          {/* Voice picker (collapsible) */}
          {showVoicePicker && (
            <div className="px-3 py-2 border-b border-white/5 bg-white/[0.02] flex items-center gap-2 text-xs">
              <label className="text-white/70 shrink-0">
                {language === 'ar' ? 'الصوت:' : 'Voice:'}
              </label>
              <select
                value={voiceId}
                onChange={(e) => setVoiceId(e.target.value)}
                className="flex-1 bg-[#0F172A] border border-white/10 text-white rounded-md px-2 py-1 text-xs"
                data-testid="assistant-voice-select"
              >
                {voices.map((v) => (
                  <option key={v.id} value={v.id}>{language === 'ar' ? (v.name_ar || v.name) : v.name}</option>
                ))}
              </select>
              <label className="flex items-center gap-1 text-white/70 cursor-pointer">
                <input type="checkbox" checked={autoplay} onChange={(e) => setAutoplay(e.target.checked)} className="accent-emerald-500" />
                <Volume2 className="h-3 w-3" />
              </label>
            </div>
          )}

          {/* Messages */}
          <div ref={listRef} className="flex-1 overflow-y-auto px-3 py-3 space-y-3" data-testid="assistant-messages">
            {messages.length === 0 && (
              <div className="h-full flex flex-col items-center justify-center text-center gap-2 px-6">
                <Sparkles className="h-8 w-8 text-amber-400" />
                <p className="text-white font-semibold text-sm">
                  {language === 'ar' ? 'كيف أقدر أساعدك اليوم؟' : 'How can I help you today?'}
                </p>
                <p className="text-xs text-white/50 leading-relaxed">
                  {language === 'ar'
                    ? 'اكتب سؤالك أو اضغط على الميكروفون لتسجيل رسالة صوتية.'
                    : 'Type your question or tap the mic to record a voice message.'}
                </p>
              </div>
            )}
            {messages.map((m, i) => (
              <div key={i} className={`flex ${m.role === 'user' ? (isRTL ? 'justify-start' : 'justify-end') : (isRTL ? 'justify-end' : 'justify-start')}`}>
                <div className={`max-w-[85%] px-3 py-2 rounded-2xl text-sm leading-relaxed ${
                  m.role === 'user'
                    ? 'bg-emerald-500 text-white rounded-ee-sm'
                    : 'bg-white/5 text-white border border-white/5 rounded-es-sm'
                }`} data-testid={`msg-${m.role}-${i}`}>
                  <p className="whitespace-pre-wrap break-words">{m.text}</p>
                  {m.audio?.b64 && m.role === 'assistant' && (
                    <button
                      onClick={() => playAudioB64(m.audio.b64, m.audio.mime)}
                      className="mt-1.5 inline-flex items-center gap-1 text-[11px] text-emerald-300 hover:text-emerald-200"
                      data-testid={`msg-play-${i}`}
                    >
                      <Volume2 className="h-3 w-3" />
                      {language === 'ar' ? 'استمع للرد' : 'Play'}
                    </button>
                  )}
                </div>
              </div>
            ))}
            {busy && (
              <div className={`flex ${isRTL ? 'justify-end' : 'justify-start'}`}>
                <div className="px-3 py-2 rounded-2xl bg-white/5 border border-white/5 flex items-center gap-2 text-white/70 text-xs">
                  <Loader2 className="h-3 w-3 animate-spin text-emerald-400" />
                  {language === 'ar' ? 'يفكّر…' : 'Thinking…'}
                </div>
              </div>
            )}
          </div>

          {/* Composer */}
          <div className="border-t border-white/10 bg-[#0F172A] p-2.5 flex items-center gap-2">
            <button
              onClick={toggleMic}
              disabled={busy && !recording}
              className={`h-10 w-10 rounded-full flex items-center justify-center shrink-0 transition ${
                recording
                  ? 'bg-red-500 text-white animate-pulse shadow-lg shadow-red-500/40'
                  : 'bg-white/5 text-white/80 hover:bg-white/10'
              } disabled:opacity-50`}
              data-testid="assistant-mic"
              aria-label={recording ? 'stop' : 'record'}
            >
              {recording ? <StopCircle className="h-5 w-5" /> : <Mic className="h-5 w-5" />}
            </button>
            <input
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyDown={(e) => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); sendText(); } }}
              placeholder={language === 'ar' ? 'اكتب رسالتك هنا…' : 'Type your message…'}
              className="flex-1 bg-[#0B1120] border border-white/10 rounded-full px-3.5 py-2 text-sm text-white placeholder:text-white/40 focus:outline-none focus:border-emerald-400"
              data-testid="assistant-input"
              disabled={busy || recording}
            />
            <button
              onClick={() => sendText()}
              disabled={!input.trim() || busy || recording}
              className="h-10 w-10 rounded-full flex items-center justify-center bg-gradient-to-br from-emerald-500 to-teal-500 text-white disabled:opacity-40 disabled:cursor-not-allowed hover:opacity-90 transition shrink-0"
              data-testid="assistant-send"
              aria-label="send"
            >
              <Send className="h-4 w-4" />
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
